'use client'

import React, { useState, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import {
  Combobox,
  ComboboxButton,
  ComboboxLabel,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react'
import classNames from 'classnames'
import Image from 'next/image'
import { useTranslation } from 'app/i18n/client'
import { Control } from 'react-hook-form'
import CheckIcon from 'public/icons/check.svg'
import ArrowDownIcon from 'public/icons/arrow-down.svg'
import ArrowUpIcon from 'public/icons/arrow-up.svg'

interface CountryOption {
  label: string
  value: string
  flag: string
}

interface FormCountrySelectProps {
  control?: Control<any>
  name: string
  label?: string | React.ReactNode
  placeHolder?: string
  disabled?: boolean
  isError?: boolean
  errorMessage?: string
  options: CountryOption[]
  value?: any
  containerClassName?: string
  containerId?: string
  buttonClassName?: string
  optionListClassName?: string
  error?: any
  defaultValue?: string
  controllerProps?: any
}

const FormCountrySelect = ({
  controllerProps,
  control,
  name,
  label,
  placeHolder = 'Select a country',
  disabled,
  isError,
  errorMessage,
  options,
  containerClassName,
  containerId,
  buttonClassName,
  optionListClassName,
  error,
  defaultValue,
}: FormCountrySelectProps) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')

  const filteredOptions = useMemo(() => {
    if (query === '') {
      return options
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return options.filter((country) =>
      new RegExp(`\\b${escapedQuery}`, 'i').test(country.label),
    )
  }, [options, query])

  return (
    <Controller
      control={control}
      render={({ field: { onChange, value } }) => {
        // Coerce to '' so the underlying Combobox/<input> stays controlled.
        // RHF returns undefined until a defaultValue or setValue lands; the
        // flip to a country code otherwise triggers React's "uncontrolled →
        // controlled" warning from headlessui's internal input.
        const safeValue = value ?? ''
        return (
          <div id={containerId} className={containerClassName}>
            <Combobox
              disabled={disabled}
              value={safeValue}
              onChange={(newValue) => {
                onChange(newValue)
                setQuery('')
              }}
            >
              {({ open }) => (
                <>
                  {label && (
                    <ComboboxLabel className="block text-sm font-medium text-gray-900 mb-2">
                      {label}
                    </ComboboxLabel>
                  )}
                  <div className="relative">
                    {/* Button Display */}
                    <ComboboxButton
                      className={classNames(
                        'w-full py-3 px-3 pr-10 text-sm text-left bg-white border border-gray-150 focus:outline-none',
                        buttonClassName,
                        {
                          'border-error': isError || error,
                          'bg-gray-100 cursor-not-allowed': disabled,
                        },
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {value && (
                          <Image
                            src={
                              options?.find((x) => x.value === value)?.flag ||
                              ''
                            }
                            alt=""
                            width={32}
                            height={24}
                            className="border border-gray-150 flex-shrink-0"
                            style={{ borderWidth: '0.1px' }}
                          />
                        )}
                        <span className="truncate">
                          {value
                            ? options?.find((x) => x.value === value)?.label
                            : placeHolder}
                        </span>
                      </div>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ArrowDownIcon
                          className={classNames(
                            'fill-current text-primary',
                            open ? 'hidden' : 'block',
                          )}
                        />
                        <ArrowUpIcon
                          className={classNames(
                            'fill-current text-primary',
                            open ? 'block' : 'hidden',
                          )}
                        />
                      </div>
                    </ComboboxButton>

                    {/* Search Input - Only shown when open */}
                    {open && (
                      <div className="absolute top-full left-0 right-0 mt-1 z-50">
                        <ComboboxInput
                          className="w-full py-2 px-3 text-sm leading-5 text-gray-900 bg-white border border-gray-150 focus:outline-none"
                          value={query}
                          onChange={(event) => setQuery(event.target.value)}
                          displayValue={() => ''}
                          placeholder={`${placeHolder}...`}
                          autoFocus
                        />
                      </div>
                    )}
                    <ComboboxOptions
                      className={classNames(
                        'absolute block max-h-60 w-full overflow-auto bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-40',
                        open ? 'top-24' : 'hidden',
                        optionListClassName,
                      )}
                    >
                      {filteredOptions.length === 0 && query !== '' ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                          {t('No result found')}
                        </div>
                      ) : (
                        filteredOptions.map((country) => (
                          <ComboboxOption
                            key={country.value}
                            className={({ active }) =>
                              classNames(
                                'relative cursor-default select-none py-2 pl-3 pr-9',
                                active
                                  ? 'bg-primary text-white'
                                  : 'text-gray-900',
                              )
                            }
                            value={country.value}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={classNames(
                                    'flex items-center gap-3 truncate',
                                    selected ? 'font-medium' : 'font-normal',
                                  )}
                                >
                                  <Image
                                    src={country.flag}
                                    alt=""
                                    width={32}
                                    height={24}
                                    className="border border-gray-150 flex-shrink-0"
                                    style={{ borderWidth: '0.1px' }}
                                  />
                                  <span>{country.label}</span>
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    <CheckIcon
                                      className={classNames(
                                        active ? 'text-white' : 'text-primary',
                                      )}
                                      aria-hidden="true"
                                    />
                                  </span>
                                )}
                              </>
                            )}
                          </ComboboxOption>
                        ))
                      )}
                    </ComboboxOptions>
                  </div>
                  {(isError || error) && (errorMessage || error?.message) && (
                    <p className="mt-2 text-xs text-error">
                      {errorMessage || t(error?.message)}
                    </p>
                  )}
                </>
              )}
            </Combobox>
          </div>
        )
      }}
      name={name}
      defaultValue={defaultValue}
      {...controllerProps}
    />
  )
}

export default FormCountrySelect
